import React, { useMemo } from 'react'
import { Controller } from 'react-hook-form'
import { Button, Flex, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import CustomSelect from 'src/app/shared/components/common/select'
import CustomInput from 'src/app/shared/components/common/input'
import CustomRangePicker from 'src/app/shared/components/common/range-picker'
import { BlackResetIcon, ResetIconDisabled, WhiteSearchIcon } from 'src/assets/icons'
import { useVoidChecksContext } from '../index.context'
import { colorText } from 'src/app/themes/primaryThemeDefault'
import { useDisableFutureDates } from 'src/app/shared/hooks/useDisableFutureDates'
import { isEmpty } from 'lodash'

const VoidChecksForm: React.FC = () => {
  const { t } = useTranslation()
  const { form, clientOptions, accountOptions, loading, searchLoading, disabled, onSubmit, onReset } = useVoidChecksContext()
  const { control, handleSubmit, formState } = form
  const { errors, isValid, isDirty } = useMemo(() => formState, [formState])
  const { disableFutureDates } = useDisableFutureDates()

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
      <Flex vertical gap={24} style={{ height: '100%' }}>
        <Flex vertical gap={24}>
          <Controller
            control={control}
            name='clientId'
            render={({ field, fieldState }) => (
              <CustomSelect
                label={t('VoidChecks:label.clientName')}
                placeholder={t('VoidChecks:placeholder.clientName')}
                value={field.value}
                onChange={field.onChange}
                options={clientOptions}
                error={fieldState.error}
                disabled={disabled}
                loading={loading}
                showSearch
              />
            )}
          />

          <Flex gap={24}>
            <Controller
              control={control}
              name='accountIds'
              render={({ field, fieldState }) => (
                <CustomSelect
                  label={t('VoidChecks:label.accountName')}
                  placeholder={t('VoidChecks:placeholder.accountName')}
                  value={field.value}
                  onChange={field.onChange}
                  options={accountOptions}
                  error={fieldState.error}
                  mode='multiple'
                  showSearch
                  haveAllOption
                  allowClear={!!field.value?.length}
                />
              )}
            />

            <Controller
              control={control}
              name='checkNumber'
              render={({ field, fieldState }) => (
                <CustomInput
                  label={t('VoidChecks:label.checkNumber')}
                  placeholder={t('VoidChecks:placeholder.checkNumber')}
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error}
                />
              )}
            />
          </Flex>

          <Flex gap={24}>
            <Controller
              control={control}
              name='date'
              render={({ field, fieldState }) => (
                <CustomRangePicker
                  label={t('VoidChecks:label.issuedDate')}
                  placeholder={[
                    t('VoidChecks:placeholder.dateFrom'),
                    t('VoidChecks:placeholder.dateTo')
                  ]}
                  value={field.value as any}
                  onChange={field.onChange}
                  disabledDate={disableFutureDates}
                  error={fieldState.error}
                />
              )}
            />

            <Controller
              control={control}
              name='amount'
              render={({ field, fieldState }) => (
                <CustomInput
                  label={t('VoidChecks:label.amount')}
                  placeholder={t('VoidChecks:placeholder.amount')}
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error}
                  suffix='$'
                />
              )}
            />
          </Flex>
        </Flex>

        <Flex
          style={{
            justifyContent: 'flex-end',
            gap: 24,
          }}
          className='button-group'
        >
          <Button
            type='default'
            className='new-account-cancel-button'
            icon={isDirty ? <BlackResetIcon /> : <ResetIconDisabled />}
            disabled={!isDirty}
            onClick={onReset}
          >
            {t('common:reset')}
          </Button>
          <Button
            type='primary'
            className='new-account-button'
            htmlType='button'
            disabled={!isValid || !isEmpty(errors) || searchLoading}
            onClick={handleSubmit(onSubmit)}
            loading={searchLoading}
            icon={<WhiteSearchIcon />}
          >
            {t('common:search')}
          </Button>
        </Flex>
      </Flex>
    </form>
  )
}

export default VoidChecksForm
